// The signed-in member: profile, access and preferences (GET/PATCH /v1/me), shared by every page
// through one cached store. The theme preference is also kept in localStorage so the first paint
// (index.html) is already in the right theme.
import { useEffect, useSyncExternalStore } from 'react';
import type { BrokerId } from '@planb/core';
import { api } from '@/lib/api';

export type Theme = 'light' | 'dark' | 'system';
export interface Prefs { theme?: Theme; home?: string; index?: string; riskPct?: number; broker?: BrokerId; dayLossLimit?: number; maxLosses?: number; guardOff?: boolean }
export interface Me {
  user: string; role: 'admin' | 'member'; status: 'pending' | 'active'; access: boolean;
  name: string; picture: string; displayName: string; avatar: string; prefs: Prefs;
  accessRequestedAt: string | null; createdAt: string | null;
}

let me: Me | null = null;
let loading: Promise<void> | null = null;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());
const normalise = (m: Me): Me => ({ ...m, prefs: m.prefs ?? {} });

function load() {
  loading ??= api('/v1/me').then(async (r) => {
    if (r.ok) { me = normalise(await r.json()); applyTheme(me.prefs.theme); emit(); }
  }).catch(() => {}).finally(() => { loading = null; });
  return loading;
}

export function useMe() {
  const v = useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => me);
  useEffect(() => { if (!me) void load(); }, []);
  return v;
}

/** Saves profile fields or preferences; resolves to an error message, or null when saved. */
export async function updateMe(patch: { displayName?: string; avatar?: string; prefs?: Prefs }): Promise<string | null> {
  const r = await api('/v1/me', { method: 'PATCH', body: JSON.stringify(patch) });
  if (!r.ok) return (await r.json().catch(() => ({}))).error ?? 'Could not save';
  me = normalise(await r.json());
  applyTheme(me.prefs.theme);
  emit();
  return null;
}

export async function requestAccess(): Promise<boolean> {
  const r = await api('/v1/access/request', { method: 'POST' });
  if (r.ok) await load();
  return r.ok;
}

export const displayName = (m: Me | null) => (m ? m.displayName || m.name || m.user.split('@')[0] : '');

// ── Theme ────────────────────────────────────────────────────────────────

const THEME_KEY = 'mhq.theme';
const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

export function resolvedTheme(t: Theme | undefined): 'light' | 'dark' {
  return t === 'dark' || (t === 'system' && media?.matches) ? 'dark' : 'light';
}

export function storedTheme(): Theme {
  try { const t = localStorage.getItem(THEME_KEY); return t === 'dark' || t === 'system' ? t : 'light'; } catch { return 'light'; }
}

let current: Theme = storedTheme();
const themeSubs = new Set<() => void>();

export function applyTheme(t: Theme | undefined) {
  current = t ?? current;
  try { localStorage.setItem(THEME_KEY, current); } catch { /* not remembered */ }
  document.documentElement.classList.toggle('dark', resolvedTheme(current) === 'dark');
  themeSubs.forEach((f) => f());
}
media?.addEventListener('change', () => { if (current === 'system') applyTheme('system'); });

/** 'light' | 'dark' as shown now; charts redraw when it changes. */
export function useResolvedTheme() {
  return useSyncExternalStore((f) => { themeSubs.add(f); return () => themeSubs.delete(f); }, () => resolvedTheme(current));
}

/** The theme toggle cycles light → dark, saving to the account when signed in. */
export function setTheme(t: Theme) {
  applyTheme(t);
  if (me) void updateMe({ prefs: { ...me.prefs, theme: t } });
}
export const currentTheme = () => current;
